import { useRef } from "react";
import { motion, useMotionValue, useSpring, useMotionTemplate, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { scrollToId } from "../lib/scroll";
import { whatsappLink } from "../lib/api";

const HERO_IMAGE = "/photos/nuggets-hand.jpg";

const lineEase = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.9, ease: lineEase, delay: 0.25 + index * 0.14 }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroSection = ({ onQuote }) => {
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [9, -9]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-11, 11]), { stiffness: 120, damping: 16 });
  const glowX = useMotionTemplate`${useTransform(mx, (v) => v * 100)}%`;
  const glowY = useMotionTemplate`${useTransform(my, (v) => v * 100)}%`;

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const barY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const handleMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden bg-[radial-gradient(circle_at_50%_0%,#FFFFFF_0%,#FAF8F5_70%,#F3EFE6_100%)]">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-28 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: lineEase, delay: 0.1 }}
            className="text-xs font-medium tracking-[0.3em] uppercase text-[#8C6D27] mb-8"
          >
            Wholesale Bullion — Nimba Range, Liberia
          </motion.p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02] tracking-tight text-[#1A1816]">
            <MaskedLine index={0}>Direct from</MaskedLine>
            <MaskedLine index={1}>
              <em className="gold-foil-text not-italic font-normal italic">Liberian mines</em>
            </MaskedLine>
            <MaskedLine index={2}>to global vaults.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: lineEase, delay: 0.85 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#6E6A63] font-light"
          >
            Pacific Gold sells investment-grade gold bars at wholesale — direct from our mines and sales
            headquarters in Liberia. Minimum order <span className="font-mono text-[#1A1816]">500 g</span>;
            prices start at <span className="font-mono text-[#1A1816]">$60,000</span>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: lineEase, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              data-testid="hero-explore-catalog-btn"
              onClick={() => scrollToId("#catalog")}
              className="group rounded-none h-14 px-9 bg-[#141312] text-[#FAF8F5] hover:bg-[#8C6D27] text-[13px] tracking-[0.16em] uppercase transition-colors duration-500"
            >
              Explore Wholesale Bars
              <ArrowDown size={15} className="ml-3 transition-transform duration-500 group-hover:translate-y-1" />
            </Button>
            <a data-testid="hero-whatsapp-btn" href={whatsappLink("Hello Pacific Gold, I would like to speak with the Head of Sales about a wholesale gold order")} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="rounded-none h-14 px-9 border-[#C5A059]/50 text-[#1A1816] hover:bg-[#C5A059]/10 hover:border-[#C5A059] text-[13px] tracking-[0.16em] uppercase transition-colors duration-500"
              >
                <Phone size={15} className="mr-3" /> Speak with Sales
              </Button>
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-[#E6E0D4] pt-7"
          >
            {[
              ["Min. order", "500 g"],
              ["Entry price", "$60,000"],
              ["Purity", "999.9 Fine"],
              ["Settlement", "Escrow-insured"],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-[10px] tracking-[0.24em] uppercase text-[#6E6A63]">{k}</p>
                <p className="font-mono text-sm mt-1 text-[#1A1816]">{v}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: barY }} className="lg:col-span-5" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: lineEase, delay: 0.5 }}>
          <div
            ref={cardRef}
            data-testid="hero-tilt-card"
            onMouseMove={handleMove}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
            className="relative aspect-[4/5] max-w-md mx-auto [perspective:1200px]"
          >
            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative h-full w-full overflow-hidden bg-[#141312] shadow-[0_10px_30px_-10px_rgba(20,19,18,0.08),0_0_1px_1px_rgba(197,160,89,0.15)]"
            >
              <img
                src={HERO_IMAGE}
                alt="Pacific Gold bar, as mined in Liberia"
                className="absolute inset-0 h-full w-full object-cover opacity-90"
                style={{ objectPosition: "50% 26%" }}
              />
              <motion.div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background: useMotionTemplate`radial-gradient(circle at ${glowX} ${glowY}, rgba(212,175,55,0.28) 0%, transparent 55%)`,
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141312]/80 via-transparent to-transparent" />
              <div className="absolute inset-4 border border-[#C5A059]/35 pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 p-7 text-[#FAF8F5]" style={{ transform: "translateZ(40px)" }}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">As mined · Assay-certified</p>
                <p className="font-serif text-2xl mt-2">Fine Gold — Direct from the Nimba Range</p>
                <p className="font-mono text-xs mt-1 text-[#FAF8F5]/70">Weighed &amp; certified · Pacific Gold Refinings, Liberia</p>
              </div>
              <div className="absolute top-6 left-6 bg-[#FAF8F5]/95 px-3 py-2" style={{ transform: "translateZ(50px)" }}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-[#8C6D27]">999.9 AU</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
